/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { PassengerType, UnitPositionType } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface TripPassengerProps {
  id: string;
  name: string;
  abbreviation: string;
  weightInKg: number | null;
  type: PassengerType;
  positionName: string | null;
  positionType: UnitPositionType | null;
  notificationAddress: string | null;
  personId: string | null;
  crewMemberId: string | null;
  tripId: string;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class TripPassengerRecord extends Record<TripPassengerProps>({
  id: null as any,
  name: null as any,
  abbreviation: null as any,
  weightInKg: null as any,
  type: null as any,
  positionName: null as any,
  positionType: null as any,
  notificationAddress: null as any,
  personId: null as any,
  crewMemberId: null as any,
  tripId: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    abbreviation: string;
    weightInKg: number | null;
    type: PassengerType;
    positionName: string | null;
    positionType: UnitPositionType | null;
    notificationAddress: string | null;
    personId: string | null;
    crewMemberId: string | null;
    tripId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): TripPassengerRecord {
    if (json == null) {
      throw new Error('TripPassengerRecord.fromJS: parameter cannot be null');
    }
    return new TripPassengerRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'TripPassengerJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'TripPassengerJson.name'),
      abbreviation: getFromOrThrow(json, (j) => j.abbreviation, 'TripPassengerJson.abbreviation'),
      weightInKg: json.weightInKg,
      type: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'type', PassengerType.PilotInCommand),
        'TripPassengerJson.type',
      ),
      positionName: json.positionName,
      positionType: json.positionType,
      notificationAddress: json.notificationAddress,
      personId: json.personId,
      crewMemberId: json.crewMemberId,
      tripId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'tripId', '00000000-0000-0000-0000-000000000000'),
        'TripPassengerJson.tripId',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'TripPassengerJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    abbreviation: string;
    weightInKg: number | null;
    type: PassengerType;
    positionName: string | null;
    positionType: UnitPositionType | null;
    notificationAddress: string | null;
    personId: string | null;
    crewMemberId: string | null;
    tripId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [TripPassengerRecord, Error[]] {
    if (json == null) {
      throw new Error('TripPassengerRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new TripPassengerRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'TripPassengerJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'TripPassengerJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      abbreviation: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.abbreviation, 'TripPassengerJson.abbreviation'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      weightInKg: tryOrDefault(
        () => json.weightInKg,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      type: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'type', PassengerType.PilotInCommand),
            'TripPassengerJson.type',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      positionName: tryOrDefault(
        () => json.positionName,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      positionType: tryOrDefault(
        () => json.positionType,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      notificationAddress: tryOrDefault(
        () => json.notificationAddress,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      personId: tryOrDefault(
        () => json.personId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      crewMemberId: tryOrDefault(
        () => json.crewMemberId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      tripId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'tripId', '00000000-0000-0000-0000-000000000000'),
            'TripPassengerJson.tripId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'TripPassengerJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: TripPassengerProps) {
    super(props);
    if (props == null) {
      throw new Error('TripPassengerRecord constructor parameter cannot be null');
    }
  }
}

export abstract class TripPassengerViewModelBase implements TripPassengerProps {
  protected readonly _props: TripPassengerProps;
  private _json?: TripPassengerProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get abbreviation(): string {
    return this._props.abbreviation;
  }
  get weightInKg(): number | null {
    return this._props.weightInKg;
  }
  get type(): PassengerType {
    return this._props.type;
  }
  get positionName(): string | null {
    return this._props.positionName;
  }
  get positionType(): UnitPositionType | null {
    return this._props.positionType;
  }
  get notificationAddress(): string | null {
    return this._props.notificationAddress;
  }
  get personId(): string | null {
    return this._props.personId;
  }
  get crewMemberId(): string | null {
    return this._props.crewMemberId;
  }
  get tripId(): string {
    return this._props.tripId;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isTypeCoPilot() {
    return this._props.type === PassengerType.CoPilot;
  }
  public get isPositionTypeCoPilot() {
    return this._props.positionType === UnitPositionType.CoPilot;
  }
  public get isCrewMember() {
    return this._props.type === PassengerType.CrewMember;
  }
  public get isTypeDriver() {
    return this._props.type === PassengerType.Driver;
  }
  public get isPositionTypeDriver() {
    return this._props.positionType === UnitPositionType.Driver;
  }
  public get isEscort() {
    return this._props.type === PassengerType.Escort;
  }
  public get isGuard() {
    return this._props.type === PassengerType.Guard;
  }
  public get isTypeOther() {
    return this._props.type === PassengerType.Other;
  }
  public get isPositionTypeOther() {
    return this._props.positionType === UnitPositionType.Other;
  }
  public get isPatient() {
    return this._props.type === PassengerType.Patient;
  }
  public get isPilotInCommand() {
    return this._props.type === PassengerType.PilotInCommand;
  }
  public get isRideAlong() {
    return this._props.type === PassengerType.RideAlong;
  }
  public get isSpecialtyTeamMember() {
    return this._props.type === PassengerType.SpecialtyTeamMember;
  }
  public get isClinician() {
    return this._props.positionType === UnitPositionType.Clinician;
  }
  public get isClinicianTrainee() {
    return this._props.positionType === UnitPositionType.ClinicianTrainee;
  }
  public get isMechanic() {
    return this._props.positionType === UnitPositionType.Mechanic;
  }
  public get isNurse() {
    return this._props.positionType === UnitPositionType.Nurse;
  }
  public get isObserver() {
    return this._props.positionType === UnitPositionType.Observer;
  }
  public get isParamedic() {
    return this._props.positionType === UnitPositionType.Paramedic;
  }
  public get isPilot() {
    return this._props.positionType === UnitPositionType.Pilot;
  }
  public get isPilotTrainee() {
    return this._props.positionType === UnitPositionType.PilotTrainee;
  }

  public isAnyType(...passengerType: PassengerType[]) {
    return passengerType.includes(this._props.type);
  }
  public isAnyPositionType(...unitPositionType: UnitPositionType[]) {
    return this._props.positionType != null && unitPositionType.includes(this._props.positionType);
  }

  constructor(props: TripPassengerProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        abbreviation: this._props.abbreviation,
        weightInKg: this._props.weightInKg,
        type: this._props.type,
        positionName: this._props.positionName,
        positionType: this._props.positionType,
        notificationAddress: this._props.notificationAddress,
        personId: this._props.personId,
        crewMemberId: this._props.crewMemberId,
        tripId: this._props.tripId,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllTripPassengerEntities = (state: AppState) =>
  state.entityStores.tripPassenger.entities;

export const selectTripPassengerById = (id: string) => (state: AppState) =>
  state.entityStores.tripPassenger.entities.get(id);

export const selectTripPassengerMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.tripPassenger.meta.get(id);

export const selectTripPassengerAndMetadataById = (id: string) =>
  createSelector(
    selectTripPassengerById(id),
    selectTripPassengerMetadataById(id),
    (tripPassenger, meta) => ({ tripPassenger, meta }),
  );

export function selectTripPassengerEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllTripPassengerEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, TripPassengerRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectTripPassengerEntitiesByName(name: string) {
  return createSelector(selectAllTripPassengerEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectTripPassengerEntitiesByAbbreviation(abbreviation: string) {
  return createSelector(selectAllTripPassengerEntities, (entities) =>
    entities.filter((e) => e.abbreviation === abbreviation).valueSeq(),
  );
}

export function selectTripPassengerEntitiesByWeightInKg(weightInKg: number) {
  return createSelector(selectAllTripPassengerEntities, (entities) =>
    entities.filter((e) => e.weightInKg === weightInKg).valueSeq(),
  );
}

export function selectTripPassengerEntitiesByType(type: PassengerType) {
  return createSelector(selectAllTripPassengerEntities, (entities) =>
    entities.filter((e) => e.type === type).valueSeq(),
  );
}

export function selectTripPassengerEntitiesByPositionName(positionName: string) {
  return createSelector(selectAllTripPassengerEntities, (entities) =>
    entities.filter((e) => e.positionName === positionName).valueSeq(),
  );
}

export function selectTripPassengerEntitiesByPositionType(positionType: UnitPositionType) {
  return createSelector(selectAllTripPassengerEntities, (entities) =>
    entities.filter((e) => e.positionType === positionType).valueSeq(),
  );
}

export function selectTripPassengerEntitiesByNotificationAddress(notificationAddress: string) {
  return createSelector(selectAllTripPassengerEntities, (entities) =>
    entities.filter((e) => e.notificationAddress === notificationAddress).valueSeq(),
  );
}

export function selectTripPassengerEntitiesByPersonId(personId: string) {
  return createSelector(selectAllTripPassengerEntities, (entities) =>
    entities.filter((e) => e.personId === personId).valueSeq(),
  );
}

export function selectTripPassengerEntitiesByCrewMemberId(crewMemberId: string) {
  return createSelector(selectAllTripPassengerEntities, (entities) =>
    entities.filter((e) => e.crewMemberId === crewMemberId).valueSeq(),
  );
}

export function selectTripPassengerEntitiesByTripId(tripId: string) {
  return createSelector(selectAllTripPassengerEntities, (entities) =>
    entities.filter((e) => e.tripId === tripId).valueSeq(),
  );
}

export function selectTripPassengerEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllTripPassengerEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
